/**
 * WhatsApp Messaging Utility Functions
 * Handles WhatsApp link generation, message formatting, auto-filling, and encoding
 */

import { Customer, LoyaltyTier, Promotion } from "../types/loyalty.types";
import { RestaurantDetails } from "../api/restaurant.api";

/**
 * IMPORTANT: Auto-fill Variables Interface
 * Represents all variables that can be automatically filled in messages
 */
export interface AutoFillVariables {
  // Customer variables
  customerName: string;
  phone: string;
  points: number;
  totalVisits: number;
  totalSpent: number;
  tier: string;
  benefits: string;

  // Restaurant variables
  restaurantName: string;
  restaurantPhone?: string;
  restaurantAddress?: string;
  restaurantEmail?: string;

  // Promotion variables
  discount?: number;
  promoCode?: string;
  offerDetails?: string;
  expiryDate?: string;
  minOrderValue?: number;

  // Expiry and points variables
  expiryPoints?: number;
  daysLeft?: number;
  [key: string]: string | number | undefined;
}

/**
 * Create auto-filled variables from customer, restaurant, and optional promotion data
 * This is the CORE function that automatically fills ALL variables without manual input
 *
 * @param customer - Customer object from database
 * @param restaurant - Restaurant details from logged-in restaurant
 * @param promotion - Optional promotion data for promotional messages
 * @param tier - Optional loyalty tier (usually from customer.loyaltyTier)
 * @returns AutoFillVariables object with all fields populated
 */
export function createAutoFilledVariables(
  customer: Customer,
  restaurant: RestaurantDetails,
  promotion?: Promotion,
  tier?: LoyaltyTier
): AutoFillVariables {
  const customerTier = tier || customer.loyaltyTier;

  return {
    // ========== CUSTOMER VARIABLES (Auto-filled from database) ==========
    customerName: customer.name || "Valued Customer",
    phone: customer.phone || "",
    points: customer.points || 0,
    totalVisits: customer.totalVisits || 0,
    totalSpent: customer.totalSpent || 0,
    tier: customerTier?.name || "Member",
    benefits: customerTier?.benefits?.join(", ") || "Exclusive perks",

    // ========== RESTAURANT VARIABLES (Auto-filled from logged-in restaurant) ==========
    restaurantName: restaurant.name || "Our Restaurant",
    restaurantPhone: restaurant.phone || "",
    restaurantAddress: restaurant.address || "",
    restaurantEmail: restaurant.email || "",

    // ========== PROMOTION VARIABLES (Auto-filled from promotion object) ==========
    discount: promotion?.value || 0,
    promoCode: promotion?.promoCode || "",
    offerDetails: promotion?.description || "",
    expiryDate: promotion?.endDate || "",
    minOrderValue: promotion?.minimumOrderValue || 0,

    // ========== EXPIRY & POINTS VARIABLES (Calculated or from customer) ==========
    // NOTE: Backend should provide these fields in Customer object
    expiryPoints: (customer as any).expiryPoints || 0,
    daysLeft: (customer as any).pointsExpireInDays || 30,
  };
}

/**
 * Calculate days until points expire
 * Helper function to compute remaining days
 *
 * @param customer - Customer object with expiryDate
 * @returns Number of days until expiry
 */
export function calculateDaysUntilExpiry(customer: Customer): number {
  const expiryDate = (customer as any).expiryDate;

  if (!expiryDate) {
    return 30; // Default to 30 days if not available
  }

  const today = new Date();
  const expiry = new Date(expiryDate);
  const diffTime = expiry.getTime() - today.getTime();
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

  return Math.max(0, diffDays);
}

/**
 * Generate a WhatsApp chat link with pre-filled message
 * Opens WhatsApp (web or mobile app) with the customer's phone number and message pre-filled
 *
 * @param phoneNumber - Customer's phone number (with or without country code)
 * @param message - Message text to be pre-filled in WhatsApp chat
 * @returns WhatsApp link URL
 */
export function generateWhatsAppLink(
  phoneNumber: string,
  message: string
): string {
  if (!phoneNumber || !message) {
    throw new Error("Phone number and message are required");
  }

  // Normalize phone number: remove spaces, dashes, parentheses
  const normalizedPhone = phoneNumber.replace(/[\s\-()]/g, "");

  // Ensure phone number starts with country code (+ prefix)
  const phoneWithCountryCode = normalizedPhone.startsWith("+")
    ? normalizedPhone
    : "+91" + normalizedPhone; // Default to India if no country code

  // Encode message for URL
  const encodedMessage = encodeURIComponent(message);

  // WhatsApp Web URL format (works on desktop and mobile)
  return `https://wa.me/${phoneWithCountryCode}?text=${encodedMessage}`;
}

/**
 * Format a message template by replacing variables with actual values
 * Variables in template should be in format: {variableName}
 *
 * @param template - Message template string with variables like "Hi {customerName}, you have {points} points!"
 * @param variables - Object containing variable values { customerName: "John", points: 500 }
 * @returns Formatted message with variables replaced
 */
export function formatMessage(
  template: string,
  variables: Record<string, string | number | undefined>
): string {
  if (!template) return "";

  let formattedMessage = template;

  // Replace each variable in the template
  Object.entries(variables).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      const regex = new RegExp(`\\{${key}\\}`, "g");
      formattedMessage = formattedMessage.replace(regex, String(value));
    }
  });

  // Clean up any remaining unreplaced variables
  formattedMessage = formattedMessage.replace(/\{[^}]+\}/g, "");

  return formattedMessage.trim();
}

/**
 * Encode message for safe URL transmission
 * Used internally by generateWhatsAppLink
 *
 * @param message - Raw message text
 * @returns URL-encoded message
 */
export function encodeMessage(message: string): string {
  return encodeURIComponent(message);
}

/**
 * Decode message from URL encoding
 * Useful for testing and debugging
 *
 * @param encodedMessage - URL-encoded message
 * @returns Decoded message text
 */
export function decodeMessage(encodedMessage: string): string {
  return decodeURIComponent(encodedMessage);
}

/**
 * Validate phone number format
 * Accepts various formats: +91-9876543210, 9876543210, +919876543210, etc.
 *
 * @param phoneNumber - Phone number to validate
 * @returns Boolean indicating if phone number is valid
 */
export function isValidPhoneNumber(phoneNumber: string): boolean {
  if (!phoneNumber) return false;

  // Remove all non-digit characters except +
  const cleaned = phoneNumber.replace(/[^\d+]/g, "");

  // Phone number should be at least 7 digits
  if (cleaned.length < 7) return false;

  // Phone number should not exceed 15 digits (international standard)
  if (cleaned.length > 15) return false;

  return true;
}

/**
 * Extract variables from a template string
 * Useful for displaying which variables are used in a template
 *
 * @param template - Message template string
 * @returns Array of variable names found in the template
 */
export function extractVariablesFromTemplate(template: string): string[] {
  if (!template) return [];

  const regex = /\{([^}]+)\}/g;
  const variables: string[] = [];
  let match;

  while ((match = regex.exec(template)) !== null) {
    variables.push(match[1]);
  }

  return [...new Set(variables)]; // Remove duplicates
}

/**
 * Truncate message if it exceeds WhatsApp character limit
 * WhatsApp has a message character limit of ~4096 characters
 *
 * @param message - Message text
 * @param maxLength - Maximum characters (default: 4096)
 * @returns Truncated message with ellipsis if needed
 */
export function truncateWhatsAppMessage(
  message: string,
  maxLength: number = 4096
): string {
  if (message.length <= maxLength) return message;
  return message.substring(0, maxLength - 3) + "...";
}

/**
 * Generate WhatsApp links for multiple customers (batch operation)
 * Returns array of links that can be used for bulk sending
 *
 * @param customers - Array of customer objects with name, phone properties
 * @param message - Message template
 * @returns Array of WhatsApp link objects
 */
export function generateBulkWhatsAppLinks(
  customers: Array<{ id: string; name: string; phone: string }>,
  message: string
): Array<{
  customerId: string;
  customerName: string;
  link: string;
  phone: string;
}> {
  return customers
    .filter((customer) => isValidPhoneNumber(customer.phone))
    .map((customer) => ({
      customerId: customer.id,
      customerName: customer.name,
      phone: customer.phone,
      link: generateWhatsAppLink(customer.phone, message),
    }));
}

/**
 * Create a WhatsApp message preview with character count
 * Useful for UI display before sending
 *
 * @param message - Message text
 * @returns Preview object with message, length, and warnings
 */
export function getWhatsAppMessagePreview(message: string): {
  message: string;
  characterCount: number;
  wordCount: number;
  isValid: boolean;
  warning?: string;
} {
  const characterCount = message.length;
  const wordCount = message.trim().split(/\s+/).length;
  let warning: string | undefined;

  if (characterCount === 0) {
    warning = "Message is empty";
  } else if (characterCount > 4096) {
    warning = "Message exceeds WhatsApp character limit (4096)";
  } else if (characterCount > 3000) {
    warning = "Message is quite long";
  }

  return {
    message,
    characterCount,
    wordCount,
    isValid: characterCount > 0 && characterCount <= 4096,
    warning,
  };
}
